import {
  AG_GRID_OPTION_MARK_KEY,
  AG_GRID_OPTION_PRICE_QUOTE_KEY,
  AG_GRID_OPTION_STRIKE_PRICE_KEY,
  OptionTypeEnum,
  columnTypes,
  type AgGridFormattedNumberProps,
  type Column,
  type ColumnDef,
  type GridOptionMarkParams,
  type GridOptionsPriceQuoteParams,
  type Leaves,
  type StrikeOptionColumn,
} from '@talos/kyoko';

import type { CellClassParams, ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { clone, concat, get, reverse } from 'lodash-es';
import { useMemo } from 'react';
import { OptionChainPrimeRFQButton } from '../components/OptionChainPrimeRFQButton';
import { OPTION_CHAIN_TOOLTIPS } from '../tooltips';
import type { OPTION_HEADER_MAP, OptionChainDataRow } from '../types';
import {
  DEFAULT_OPTION_COLUMNS_IS_VISIBLE,
  OPTION_BID_IV,
  OPTION_BID_PRICE,
  OPTION_BID_SIZE,
  OPTION_DELTA,
  OPTION_GAMMA,
  OPTION_LAST_PRICE,
  OPTION_MARK,
  OPTION_OFFER_IV,
  OPTION_OFFER_PRICE,
  OPTION_OFFER_SIZE,
  OPTION_OPEN_INTEREST,
  OPTION_POSITION,
  OPTION_PRIME_RFQ_BUTTON,
  OPTION_RHO,
  OPTION_SPREAD,
  OPTION_THETA,
  OPTION_VEGA,
  OPTION_VOLUME,
  getOptionColumnLabel,
} from '../types';
import { getOptionCellClassName, getOptionColumnId } from '../utils';

const getOptionType = (fieldName: string | undefined): OptionTypeEnum => {
  return fieldName?.startsWith('Call.') ? OptionTypeEnum.Call : OptionTypeEnum.Put;
};

/**
 * https://docs.google.com/spreadsheets/d/16Sj2r2mf6qTiPwT3dQVcXgiET4ALnTfnCtuEbE3b79A/
 */
export function useOptionChainColumns(
  /**
   * Record of fields to determine which columns should be visible,
   * true => visible and false => hidden. If a entry does not exist
   * in the record, the column defaults to be shown.
   */
  initialColumnDefsVisibility: Record<keyof typeof OPTION_HEADER_MAP, boolean> = DEFAULT_OPTION_COLUMNS_IS_VISIBLE
) {
  const columnArray: Column[] = useMemo(() => {
    const reverseable: ColDef[] = [
      {
        field: OPTION_OPEN_INTEREST,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: { increment: '0.01' },
      },
      {
        field: OPTION_LAST_PRICE,
        cellRenderer: AG_GRID_OPTION_PRICE_QUOTE_KEY,
        cellRendererParams: ({ data }: ICellRendererParams<OptionChainDataRow>) =>
          ({
            currency: data?.Currency,
            type: undefined,
            underlyingQuoteCurrency: data?.UnderlyingQuoteCurrency,
            underlyingPrice: data?.UnderlyingPrice,
            expiration: data?.Expiration,
            quoteCurrency: data?.QuoteCurrency,
          }) satisfies GridOptionsPriceQuoteParams,
        minWidth: 90,
      },
      // Greeks
      {
        field: OPTION_RHO,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: { increment: '0.01' },
      },
      {
        field: OPTION_VEGA,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: { increment: '0.01' },
      },
      {
        field: OPTION_GAMMA,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: { increment: '0.000001' },
      },
      {
        field: OPTION_THETA,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: { increment: '0.01' },
      },
      {
        field: OPTION_DELTA,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: { increment: '0.01' },
      },

      {
        field: OPTION_VOLUME,
        flex: 5,
        cellRenderer: 'formattedNumberColumn',
        cellRendererParams: ({ data, context }: ICellRendererParams<OptionChainDataRow>) => {
          if (!data) {
            return;
          }
          const currency = data.Currency;
          const increment =
            (currency && context.current.currenciesBySymbol?.get(currency)?.DefaultIncrement) || '0.0001';

          return {
            unit: currency ?? '',
            increment: increment,
          } satisfies AgGridFormattedNumberProps;
        },
        minWidth: 100,
      },
    ];

    // First we define the stat columns that are available for both sides
    // Note that the field will be mutated later on
    const statisticColumns: ColDef[] = [
      // Bid
      {
        field: OPTION_BID_SIZE,
      },
      columnTypes.percent({
        field: OPTION_BID_IV,
        type: 'percent',
        params: { increment: '0.01' },
        maxWidth: null,
      }),
      {
        field: OPTION_BID_PRICE,
        cellRenderer: AG_GRID_OPTION_PRICE_QUOTE_KEY,
        cellRendererParams: ({ data, colDef }: ICellRendererParams<OptionChainDataRow>) => {
          const side = getOptionType(colDef?.field);
          const markPrice = get(data, `${side}.Mark.MarkPrice`);

          return {
            currency: data?.Currency,
            type: 'Bid',
            underlyingQuoteCurrency: data?.UnderlyingQuoteCurrency,
            underlyingPrice: data?.Market === 'deribit' ? data?.IndexPrice : data?.UnderlyingPrice,
            expiration: data?.Expiration,
            quoteCurrency: data?.QuoteCurrency,

            highlight: true,
            markPrice,
          } satisfies GridOptionsPriceQuoteParams;
        },
        minWidth: 110,
      },

      // Spread
      {
        field: OPTION_SPREAD,
        cellRenderer: AG_GRID_OPTION_PRICE_QUOTE_KEY,
        cellRendererParams: ({ data }: ICellRendererParams<OptionChainDataRow>) =>
          ({
            currency: data?.Currency,
            type: undefined,
            underlyingQuoteCurrency: data?.UnderlyingQuoteCurrency,
            underlyingPrice: data?.UnderlyingPrice,
            expiration: data?.Expiration,
            quoteCurrency: data?.QuoteCurrency,
          }) satisfies GridOptionsPriceQuoteParams,
        minWidth: 90,
      },

      // Mark
      {
        field: OPTION_MARK,
        cellRenderer: AG_GRID_OPTION_MARK_KEY,
        cellRendererParams: ({ data }: ICellRendererParams<OptionChainDataRow>) =>
          ({ currency: data?.QuoteCurrency }) satisfies GridOptionMarkParams,
        valueFormatter: ({ value }) => {
          if (value && typeof value === 'object' && 'MarkPrice' in value) {
            return value.MarkPrice;
          }
          return value;
        },
        minWidth: 90,
      },
      // Offer
      {
        field: OPTION_OFFER_PRICE,
        cellRenderer: AG_GRID_OPTION_PRICE_QUOTE_KEY,
        cellRendererParams: ({ data, colDef }: ICellRendererParams<OptionChainDataRow>) => {
          const side = getOptionType(colDef?.field);
          const markPrice = get(data, `${side}.Mark.MarkPrice`);

          return {
            currency: data?.Currency,
            type: 'Offer',
            underlyingQuoteCurrency: data?.UnderlyingQuoteCurrency,
            underlyingPrice: data?.Market === 'deribit' ? data?.IndexPrice : data?.UnderlyingPrice,
            expiration: data?.Expiration,
            quoteCurrency: data?.QuoteCurrency,
            highlight: true,
            markPrice,
          } satisfies GridOptionsPriceQuoteParams;
        },
        minWidth: 110,
      },
      columnTypes.percent({
        field: OPTION_OFFER_IV,
        type: 'percent',
        params: { increment: '0.01' },
        maxWidth: null,
      }),
      {
        field: OPTION_OFFER_SIZE,
      },
    ];

    const positionColumn = columnTypes.size({
      type: 'size',
      field: OPTION_POSITION,
      flex: 5,
      sortable: false,
      suppressHeaderMenuButton: true,
      frozen: true,
      // Always show position column
      hide: false,
      params: {
        highlightNegative: true,
        showCurrencyForNullPosition: false,
        trimTrailingZeroes: true,
      },
      maxWidth: null,
    });

    const getPrimeRFQColumn = (side: OptionTypeEnum) =>
      ({
        colId: getOptionColumnId(side, OPTION_PRIME_RFQ_BUTTON),
        headerName: 'RFQ',
        width: 80,
        headerClass: `ag-center-aligned-cell`,
        cellClass: `${getOptionCellClassName(side)} ag-${side === 'Call' ? 'right' : 'left'}-aligned-cell`,
        suppressHeaderMenuButton: true,
        sortable: false,
        hide: false,
        // For simplicity, put the entire data object into the value to propagate properly into the cell renderer
        valueGetter: ({ data }: ValueGetterParams<OptionChainDataRow>): OptionChainDataRow | undefined => data,
        cellRenderer: ({ value }: ICellRendererParams<OptionChainDataRow, OptionChainDataRow>) => {
          if (!value) {
            return null;
          }

          return <OptionChainPrimeRFQButton row={value} side={side} />;
        },
      }) satisfies ColDef;

    const mapStatisticColumn = (c: ColDef<OptionChainDataRow>, side: OptionTypeEnum): ColDef => {
      const field = c.field as keyof typeof OPTION_HEADER_MAP | undefined;
      return {
        minWidth: 60,
        ...c,
        // Fallback flex, to lessen or increase size of columns, do it relative to fallback.
        flex: c.flex ?? 10,
        resizable: false,
        suppressHeaderMenuButton: true,
        headerTooltip: field ? get(OPTION_CHAIN_TOOLTIPS, field) : undefined,
        headerName: field ? getOptionColumnLabel(field) : c.headerName || '',
        // if hide defined, use it.
        hide: c.hide !== undefined ? c.hide : (field && !initialColumnDefsVisibility[field]) || false,
        sortable: false,
        cellClass: (p: CellClassParams<OptionChainDataRow>) =>
          `${getOptionCellClassName(side)} ${
            typeof c.cellClass === 'function' ? c.cellClass(p) : (c.cellClass ?? '')
          } ag-right-aligned-cell`,
        headerClass: 'ag-right-aligned-cell',
        field: getOptionColumnId(side, field),
        colId: getOptionColumnId(side, field),
      };
    };

    function getSideColumns(side: OptionTypeEnum): ColDef<OptionChainDataRow>[] {
      if (side === OptionTypeEnum.Call) {
        const callStatCols = concat(reverseable, statisticColumns, [positionColumn]).map(c =>
          mapStatisticColumn(c, side)
        );
        const callActionCols = [getPrimeRFQColumn(side)];
        return [...callStatCols, ...callActionCols];
      } else {
        const putStatCols = concat([positionColumn], statisticColumns, reverse(clone(reverseable))).map(c =>
          mapStatisticColumn(c, side)
        );
        const putActionCols = [getPrimeRFQColumn(side)];
        return [...putActionCols, ...putStatCols];
      }
    }

    return [
      {
        type: 'optionSide',
        id: 'calls',
        title: 'Calls',
        children: getSideColumns(OptionTypeEnum.Call),
        frozen: true,
        flex: 1,
      },
      {
        field: 'Expiration',
        // Add hidden group expiration which is used for grouping under whatever column that specifies showRowGroup:true (StrikePrice)
        // https://www.ag-grid.com/react-data-grid/grouping-custom-group-columns/
        rowGroup: true,
        hide: true,
        sortIndex: 0,
        sort: '+',
        type: 'text',
      },
      {
        field: 'StrikePrice',
        title: 'Strike',
        type: AG_GRID_OPTION_STRIKE_PRICE_KEY,
        suppressHeaderMenuButton: true,
        frozen: true,
        width: 100,
        sortIndex: 1,
        sortable: false,
        sort: '+',
        headerClass: 'ag-center-aligned-cell',
        // Make grouping appear at StrikePrice column.
        showRowGroup: true,
        description: OPTION_CHAIN_TOOLTIPS['StrikePrice'],
        params: {
          currencyField: 'UnderlyingQuoteCurrency' satisfies Leaves<OptionChainDataRow>,
        } satisfies StrikeOptionColumn['params'],
      },
      {
        type: 'optionSide',
        id: 'puts',
        title: 'Puts',
        children: getSideColumns(OptionTypeEnum.Put),
        frozen: true,
        flex: 1,
      },
    ];
  }, [initialColumnDefsVisibility]) satisfies ColumnDef<OptionChainDataRow>[];

  return columnArray;
}
